import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActionSheetController, Events, PopoverController, NavController, Tabs } from "ionic-angular";
import { cloneDeep } from 'lodash';

import { ServicesPage } from "../../pages/services/services";
import { AdvancedBookingsPage } from "../../pages/advanced-bookings/advanced-bookings";
import { RegisterGuestServiceProvider } from "../register-guest-service/register-guest-service";
import { ApiErrorHandlerProvider } from "../api-error-handler/api-error-handler";
import { CompleteBookingModalPage } from "../../pages/complete-booking-modal/complete-booking-modal";
import { CancelModalPage } from "../../pages/cancel-modal/cancel-modal";
import { CancelledBookingsPage } from "../../pages/cancelled-bookings/cancelled-bookings";
import { ReschedulePage } from '../../pages/reschedule/reschedule';
import { BarberBookingNewPage } from '../../pages/barber-booking-new/barber-booking-new';

@Injectable()
export class BookingServiceProvider {

  constructor(
    private http: HttpClient,
    private actionSheetCtrl: ActionSheetController,
    private registerGuestService: RegisterGuestServiceProvider,
    private apiErrorHandler: ApiErrorHandlerProvider,
    private popoverCtrl: PopoverController,
    public events: Events,
  ) {

  }
  public assignServices(staff): any[] {
    // Track todays booking service name
    if (!staff.services || !staff.todays_bookings || !staff.todays_bookings.length) { return; }
    const services = staff.services;
    const todayServices = staff.todays_bookings[0].services;
    const newStaffServices = todayServices.map((service) => {
      if (!service.name) {
        const global_service = (services.find((serv) => serv.service.id === service.service_id)) ? (services.find((serv) => serv.service.id === service.service_id)).service : null;
        service = Object.assign({}, service, global_service);
      }
      return service;
    });
    return newStaffServices;
  }
  bookingsPaidForByUser(userId, pageNumber: number = 1) {

    return new Promise((resolve, reject) => {

      let endpoint = `/customers/${userId}/bookings/payment-history`;

      if (pageNumber !== 1) {
        endpoint += '?page=' + pageNumber;
      }

      this.http.get(endpoint).subscribe((bookings: any) => {

        return resolve(bookings);

      }, error => {

        return reject(error.message);

      });

    });

  }

  getBodyForBooking(booking: any = null, services, shop, barber, bookingType, guestUser: any = null, advancedBooking: any = null, byStaff: boolean = false, fromLobby: boolean = false) {
    return new Promise((resolve, reject) => {

      let tuid = localStorage.getItem('tuid');
      let currentTeamId = localStorage.getItem('currentTeamId');

      let body: any = {
        team: shop.id,
        teamMember: barber,
        last_booking_team_id: shop.id,
        termsAccepted: true,
        type: bookingType,
        user: tuid,
        services: services.services
      };

      // Normal booking by customer from the app
      if (!advancedBooking && !byStaff && !fromLobby && !booking) {
        // If any issue occurred while adding Queue then remove below line. 13/07/2023
        body.forceAdd = true;
        return resolve(body);
      }

      if (guestUser) {
        body.user = guestUser.id;
      }

      // If advanced booking pass the requested booking date and time
      if (advancedBooking) {
        body.bookingDate = advancedBooking.date;
        body.bookingTime = advancedBooking.time;
        body.overrideMaxQueue = true;

        if (!byStaff) {
          return resolve(body);
        }

        // Advanced Booking by staff
        body.last_booking_team_id = currentTeamId;
        body.team = currentTeamId;
        body.overrideMaxQueue = true;
        body.forceAdd = true;
        return resolve(body);
      }

      // Standard Booking by staff
      if (byStaff && !advancedBooking && !booking) {
        body.last_booking_team_id = currentTeamId;
        body.team = currentTeamId;
        body.overrideMaxQueue = true;

        // Only show the modal if the guest user hasn't been created yet
        return resolve(body);
      }

      // Rebook a time slot
      if (booking && !fromLobby) {
        body.last_booking_team_id = booking.team_id;
        body.existingBookingId = booking.id;
        body.existingOriginalBookingLength = booking.available_duration;
        body.existingBookingLength = booking.available_duration;
        body.totalBookingLength = this.getTotalBookingLength(services.services);
        body.overrideMaxQueue = true;
        body.estimated_chair_time_string = booking.estimated_chair_time_string;
        body.time_to_chair_in_mins = booking.time_to_chair_in_mins;
        body.type = 2;

        return resolve(body);
      }

      // Booking from the lobby by a customer
      if (fromLobby) {
        if (booking) {
          body.last_booking_team_id = booking.team_id;
          body.existingBookingId = booking.id;
          body.existingOriginalBookingLength = booking.available_duration;
          body.existingBookingLength = booking.available_duration;
          body.totalBookingLength = this.getTotalBookingLength(services.services);
          body.overrideMaxQueue = true;
          body.estimated_chair_time_string = booking.estimated_chair_time_string;
          body.time_to_chair_in_mins = booking.time_to_chair_in_mins;
          body.type = 2;
        }

        body.team = currentTeamId;
        return resolve(body);
      }
    });
  }

  getTotalBookingLength(services) {
    let total = 0;
    services.forEach(function (service) {
      total += service.duration * service.quantity;
    });

    return total;
  }

  getServicesTotal(booking) {
    let total = 0;
    if (booking && booking.services && booking.services.length) {
      booking.services.forEach(function (service) {
        total += service.quantity * service.price;
      });
    }
    return total;
  }

  getTotalCost(booking) {

    // If the total_paid is set on the booking then we know the booking has been paid for already
    // and we don't need to iterate over the services and can simply return the total_paid
    // the getTotalCost is for legacy bookings that weren't paid for in app
    if (booking.total_paid) return booking.total_paid;

    // Otherwise iterate over the services to calculate total service price
    let total = this.getServicesTotal(booking);

    return total;
  }


  getTotalCostMinusLoyalty(booking) {

    let total = this.getServicesTotal(booking);

    // Apply loyalty to amount paid
    let loyalty_points_value = (booking.loyalty_points_value ? booking.loyalty_points_value : 0);
    let total_remaining_after_loyalty_applied = (total - loyalty_points_value);
    total = total_remaining_after_loyalty_applied <= 0 ? 0 : total_remaining_after_loyalty_applied;

    return total;

  }

  makeBooking(booking, navCtrl: NavController = null, byStaff: boolean = false, fromLobby: boolean = false, totalRedemptionApplied: any = null, fromStaffPanel: boolean = false, dealer: 'barber' | 'barmen') {
    return new Promise((resolve, reject) => {

      if (totalRedemptionApplied && totalRedemptionApplied > 0) {
        booking.total_redemption_applied = totalRedemptionApplied;
      }
      this.http.post(`/bookings`, booking)
        .subscribe((booking: any) => {
          return resolve(booking);
        }, error => {

          let errors: any;

          // If there is an advanced booking and the service time doesn't fit we should give the
          // user the option of joining the end of the queue
          if (error.error.validator === 'validateStandardBookingFitsBetweenAdvancedBookings') {

            let buttons = [];

            // Only allow the customer to choose another barber
            if (!byStaff || byStaff && fromStaffPanel) {
              buttons.push({
                text: `Choose another ${dealer}`,
                handler: () => {
                  this.events.publish('validateStandardBookingFitsBetweenAdvancedBookings:chooseAnotherBarber', booking);
                }
              });
            }

            buttons.push({
              text: 'Change service time',
              handler: () => {
                this.events.publish('validateStandardBookingFitsBetweenAdvancedBookings:changeServiceTime', booking);
              }
            });

            // Give the barber the ability to force add the booking into the queue
            if (byStaff) {
              buttons.push({
                text: 'Force add to queue',
                handler: () => {
                  booking.forceAdd = true;
                  this.events.publish('validateStandardBookingFitsBetweenAdvancedBookings:forceAddToQueue', booking);
                }
              });
            }

            buttons.push({
              role: 'cancel',
              text: 'Cancel',
              handler: () => {
                this.events.publish('booking:backToStart', booking);
              }
            });


            let availableSlots = [];

            error.error.availableSlots.forEach(slot => {

              let text = 'Join the queue at ' + slot.humanized_time;

              if (byStaff) {
                text = 'Add to queue at ' + slot.humanized_time;
              }

              availableSlots.push({
                text: text,
                handler: () => {

                  booking.bookingDate = slot.date;
                  booking.bookingTime = slot.time;
                  booking.type = 2;
                  this.events.publish('validateStandardBookingFitsBetweenAdvancedBookings:joinAvailableSlot', booking, slot);

                }
              });

            });

            errors = this.apiErrorHandler.handleError(error, {
              message: error.error.message,
              buttons: availableSlots.concat(buttons)
            });

          } else if (
            error.error.validator === 'validateStandardBookingEndsBeforeTeamMemberEndTime' ||
            error.error.validator === 'validateStandardBookingEndsBeforeTeamEndTime'
          ) {

            let buttons = [];

            // Give the barber the ability to force add the booking into the queue
            if (byStaff) {
              buttons.push({
                text: 'Force add to queue',
                handler: () => {
                  booking.forceAdd = true;
                  this.events.publish('booking:forceAddToQueue', booking);
                }
              });
            }

            if (!byStaff || byStaff && fromStaffPanel) {
              buttons.push({
                text: `Choose another ${dealer}`,
                handler: () => {
                  this.events.publish('booking:chooseAnotherBarber', booking);
                }
              });
            }

            buttons.push({
              text: 'Change service time',
              handler: () => {
                this.events.publish('booking:changeServiceTime', booking);
              }
            });

            buttons.push({
              text: 'Cancel',
              handler: () => {
                this.events.publish('booking:backToStart', booking);
              }
            });

            errors = this.apiErrorHandler.handleError(error, {
              message: error.error.message,
              buttons: buttons
            });

          } else if (error.error.validator === 'validateReplacedBookingFitsAvailableSlot') {

            let buttons = [];

            // Give the barber the ability to force add the booking into the queue
            if (byStaff) {
              buttons.push({
                text: 'Force add to queue',
                handler: () => {
                  booking.forceAdd = true;
                  this.events.publish('booking:forceAddToQueue', booking);
                }
              });
            }

            if (!byStaff || byStaff && fromStaffPanel) {
              buttons.push({
                text: `Choose another ${dealer}`,
                handler: () => {
                  this.events.publish('validateReplacedBookingFitsAvailableSlot:chooseAnotherBarber', booking);
                }
              });
            }

            buttons.push({
              text: 'Change service time',
              handler: () => {
                this.events.publish('booking:changeServiceTime', booking);
              }
            });

            buttons.push({
              text: 'Cancel',
              handler: () => {
                this.events.publish('validateReplacedBookingFitsAvailableSlot:cancel', booking);
              }
            });

            errors = this.apiErrorHandler.handleError(error, {
              message: error.error.message,
              buttons: buttons
            });

          } else {

            errors = this.apiErrorHandler.handleError(error, {
              message: error.error.message,
              buttons: [{
                text: 'Ok',
                handler: () => {
                  this.events.publish('validateStandardBookingFitsBetweenAdvancedBookings:apiError', booking);
                }
              }]
            });

          }

          return reject(errors);

        }, () => {
          return resolve(true);
        });

    });

  }

  setServiceTotals(services, shop, byStaffOrFromLobby: boolean = false) {

    // If the shop allows in app payments we should apply the in app payment service fee, if it does not then we default to 0
    // we should also only charge the customer if the barbershop has chosen to pass this fee to the customer
    services.serviceFee = shop.settings.allow_pay_in_app ? Number(shop.settings.platform_service_fee) : 0;

    services.totalCost = 0;
    services.totalServiceCost = 0;
    services.availableTime = 0;
    services.services.forEach(service => {
      services.totalServiceCost += (service.price * service.quantity);
      services.totalCost += (service.price * service.quantity);
      services.availableTime += (service.duration * service.quantity);
    });

    // If there has been services added apply the service fee to the total cost
    if (services.services.length > 0 && shop.settings.pass_platform_service_fee_to_customer && !byStaffOrFromLobby) services.totalCost += services.serviceFee;

    return services;

  }

  gotoMyBookings(navCtrl) {
    // Pop to the shop page
    navCtrl.popTo(navCtrl.getByIndex(1));

    // Select the bookings tab
    let tabBar: Tabs = navCtrl.parent;

    if (tabBar) {
      tabBar.select(1);
    }
    return Promise.resolve();
  }

  bookingSuccessful(booking, payment: any = null, shopType: 'barber' | 'coffe') {
    return new Promise((resolve, reject) => {
      localStorage.setItem('lastBookingTeamId', booking.team_id); //at this place was booking.id, i changed to team_id
      localStorage.setItem('shopType', shopType);
      // If a payment has been sent with the booking success then we should update the booking
      if (payment) {
        this.updateBookingWithPaymentDetails(booking.id, payment).then(booking => {
          return resolve(booking);
        }).catch(error => {
          return reject(error);
        });
      } else {
        return resolve(booking);
      }
    });
  }

  updateBookingWithPaymentDetails(bookingId, payment) {
    return new Promise((resolve, reject) => {
      // return resolve([booking, payment]);
      this.http.post(`/bookings/${bookingId}/payment`, {
        'payment': payment
      }).subscribe((booking: any) => {
        return resolve(booking);
      }, error => {

        return reject(error.message);

      });

    });

  }

  cancelBooking(booking) {
    return new Promise(resolve => {
      let bookingCancelModal = this.popoverCtrl.create(CancelModalPage, { booking: booking }, {
        cssClass: 'cancel-booking-modal',
        enableBackdropDismiss: false
      });
      bookingCancelModal.present();
      bookingCancelModal.onDidDismiss((booking) => {
        if (booking) {
          this.events.publish('booking:cancelled', booking);
          this.events.publish('booking:badge-', booking);
          return resolve(booking);
        }
      })
    });
  }

  reScheduleBooking(booking) {
    return new Promise(resolve => {
      let bookingCancelModal = this.popoverCtrl.create(ReschedulePage, { booking: booking }, {
        cssClass: 're-schedule-booking-modal',
        enableBackdropDismiss: false
      });
      bookingCancelModal.present();
      bookingCancelModal.onDidDismiss((booking) => {
        if (booking) {
          this.events.publish('booking:cancelled', booking);
          this.events.publish('booking:badge-', booking);
          return resolve(booking);
        }
      })
    });
  }



  unsubscribeFromEvents() {
    this.events.unsubscribe('validateStandardBookingFitsBetweenAdvancedBookings:cancel');
    this.events.unsubscribe('validateStandardBookingFitsBetweenAdvancedBookings:chooseAnotherBarber');
    this.events.unsubscribe('validateStandardBookingFitsBetweenAdvancedBookings:changeServiceTime');
    this.events.unsubscribe('validateStandardBookingFitsBetweenAdvancedBookings:forceAddToQueue');
    this.events.unsubscribe('validateStandardBookingFitsBetweenAdvancedBookings:joinAvailableSlot');
    this.events.unsubscribe('validateStandardBookingFitsBetweenAdvancedBookings:apiError');
    this.events.unsubscribe('validateStandardBookingEndsBeforeTeamOrMemberEndTime:forceAddToQueue');
    this.events.unsubscribe('validateReplacedBookingFitsAvailableSlot:cancel');
    this.events.unsubscribe('validateReplacedBookingFitsAvailableSlot:chooseAnotherBarber');
    this.events.unsubscribe('booking:forceAddToQueue');
    this.events.unsubscribe('booking:changeServiceTime');
    this.events.unsubscribe('booking:backToStart');
    this.events.unsubscribe('booking:chooseAnotherBarber');

  }

  registerGuestAndPushBookingProcess(navCtrl, shop, barber, fromStaffPanel: boolean = false, isFromMyqueue: boolean = false) {

    return new Promise((resolve, reject) => {

      this.registerGuestService.register().then(guestUser => {

        this.getTodaysCancelledBookingsForTeamUser(shop.id, barber.id).then(todaysCancelledBookings => {

          barber.todays_cancelled_bookings = todaysCancelledBookings;
          if (isFromMyqueue) {
            return this.getShop(shop.id).then((shop) => {
            navCtrl.push(BarberBookingNewPage, {
              barber: barber,
              shop: shop,
              guestUser: guestUser,
              byStaff: true,
              fromStaffPanel: fromStaffPanel
            });
          });
          }

          if (barber.todays_cancelled_bookings.length > 0) {
            navCtrl.push(CancelledBookingsPage, {
              barber: barber,
              shop: shop,
              guestUser: guestUser,
              byStaff: true,
              fromStaffPanel: fromStaffPanel
            })
          } else {
            navCtrl.push(ServicesPage, {
              barber: barber,
              shop: shop,
              guestUser: guestUser,
              byStaff: true,
              fromStaffPanel: fromStaffPanel
            })
          }

        });

      }).catch(_ => {

      });

    });

  }

  addToQueue(navCtrl, team, barber, fromStaffPanel: boolean = false, isFromMyqueue: boolean = false) {
    this.events.publish('staff:disableRefresh');
    // if (barber.advanced_booking_hours && barber.advanced_booking_hours.length > 0) {
    //   this.presentActionSheet(navCtrl, team, barber, fromStaffPanel, isFromMyqueue);
    // } else {

      this.registerGuestAndPushBookingProcess(navCtrl, team, barber, fromStaffPanel, isFromMyqueue);

    // }
  }



  presentActionSheet(navCtrl, team, barber, fromStaffPanel: boolean = false, isFromMyqueue: boolean = false) {
    let actionSheet = this.actionSheetCtrl.create({
      title: 'Choose your booking type',
      buttons: [
        {
          text: 'Standard Booking',
          handler: () => {
            actionSheet.dismiss().then(_ => {
              this.registerGuestAndPushBookingProcess(navCtrl, team, barber, fromStaffPanel, isFromMyqueue);
            });
            return false;
          }
        },
        {
          text: 'Advanced Booking',
          handler: () => {
            actionSheet.dismiss().then(_ => {
              this.registerGuestService.register().then(guestUser => {
                if (!isFromMyqueue) {
                  this.openPage(navCtrl, AdvancedBookingsPage, guestUser, team.id, barber);
                } else {
                  return this.getShop(team.id).then((shop) => {
                    return navCtrl.push(BarberBookingNewPage, {
                      barber: barber,
                      shop: shop,
                      services: barber.services,
                      byStaff: true,
                      guestUser: guestUser,
                      fromStaffPanel: fromStaffPanel
                    })
                  });
                }
              }).catch(_ => {

              });
            });
            return false;
          }
        },
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
          }
        }
      ]
    });
    actionSheet.present();
  }

  openPage(navCtrl, page, guestUser, teamId, barber) {
    return this.getShop(teamId).then((shop) => {
      return navCtrl.push(page, {
        barber: barber,
        shop: shop,
        services: barber.services,
        byStaff: true,
        allowDateSelection: false,
        guestUser: guestUser
      })
    })
  }

  getShop(id) {
    return new Promise((resolve, reject) => {
      this.http.get(`/teams/${id}`)
        .subscribe((shop: any) => {
          resolve(shop);
        }, error => {
          this.apiErrorHandler.handleError(error);
          reject(error);
        });
    });
  }

  confirmBookingCompletion(booking, barber) {
    return new Promise((resolve, reject) => {
      let bookingCompletionModal = this.popoverCtrl.create(CompleteBookingModalPage,
        {
          booking: booking,
          barber: barber
        },
        {
          cssClass: 'complete-booking-modal',
          enableBackdropDismiss: false
        });

      bookingCompletionModal.present();

      // Add the completed booking to the completed bookings array
      bookingCompletionModal.onDidDismiss((res) => {
        if (res && (res.id === booking.id)) {
          resolve(booking)
        }
        resolve(res);
      })
    })
  }

  getTodaysCancelledBookingsForTeamUser(teamId: any, tuid: any) {
    return new Promise((resolve, reject) => {
      if (!tuid || !teamId) {
        return reject();
      }
      this.http.get('/teams/' + teamId + '/bookings/' + tuid + '/cancelled-today').subscribe(res => {
        return resolve(res);
      }, error => {
        return reject(this.apiErrorHandler.handleError(error));
      });

    });
  }

  getAmountDue(booking) {
    return (this.getTotalCostMinusLoyalty(booking) + Number(booking.platform_service_fee)) - Number(booking.total_paid) - (booking.coupon_amount != null ? Number(booking.coupon_amount) : 0);
  }

  getAmountPaid(booking) {

    if (booking.total_paid) {
      return booking.total_paid;
    }

    if (booking.loyalty_points_redeemed && this.getAmountDue(booking) <= 0) {
      return this.getTotalCostMinusLoyalty(booking);
    }

    if (!booking.total_paid && !booking.loyalty_points_redeemed) {
      return 0.00;
    }

  }

  getDepositPaymentAmount(shop) {
    return this.parseNumber(shop.settings.pay_in_app_deposit_payment_amount);
  }

  getDepositPaymentAmountWithPlatformServiceFee(shop, totalCost) {
    let depositPaymentAmount = this.getDepositPaymentAmount(shop) + this.parseNumber(shop.settings.platform_service_fee);
    return depositPaymentAmount < totalCost ? depositPaymentAmount : totalCost;
  }

  parseNumber(num) {
    return Number(num);
  }
  public proccessCoffeShopServices(value: any[], id?: number): any {
    let services = cloneDeep(value.filter((service) => service.service_type === 0));
    const sizes = cloneDeep(value.filter((service) => service.service_type === 1));
    const doppings = cloneDeep(value.filter((service) => service.service_type === 2));
    services = services.map((service) => {
      const size = sizes.find((size) => Number(size.group_id) === Number(service.group_id) && Number(size.parent_service_id) === Number(service.service_id));
      if (size) {
        service.name = `${service.quantity} x ${service.name} (${size.size})`;
        service.price = Number(service.price) + Number(size.price);
      } else if (service.size) {
        service.size ? service.name = `${service.quantity} x ${service.name} (${service.size})` : null;
      } else {
        service.name = `${service.quantity} x ${service.name}`;
      }

      doppings.forEach((dopping) => {
        if (Number(dopping.group_id) === Number(service.group_id) && Number(dopping.parent_service_id) === Number(service.service_id)) {
          const kind = dopping.kind ? `(${dopping.kind})` : '';
          service.name += `\n ${dopping.quantity} x ${dopping.name}${kind}`;
          service.price = Number(service.price) + (Number(dopping.price) * Number(dopping.quantity));
        }
      });
      return service;
    });
    return services;
  }

    getAdvanceBookings(barber_Id,selectedDate) {
    return new Promise((resolve, reject) => {
      this.http.get(`/getAdvBooking/${barber_Id}`,{ params : { bookingDate : selectedDate }}).subscribe((barber: any) => {
        return resolve(barber);
      }, error => {
        let errors = this.apiErrorHandler.handleError(error);
        return reject(errors);
      })
    })
  }
}



// WEBPACK FOOTER //
// ./src/providers/booking-service/booking-service.ts